import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { TeamMember } from "../models/TeamMember";
import { TeamMemberService } from "../services/TeamMemberService";
import { useMatter } from "./MatterContext";
import { useAuth } from "./AuthContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { Invitation } from "../models/Invitation";

interface Errors {
  list?: Error | null;
  details?: Error | null;
  invitations?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
  invitations?: boolean;
}

interface TeamMemberContextProps {
  invitations: Invitation[];
  teamMembers: TeamMember[];
  createTeamMember: (
    fileData: Omit<
      TeamMember,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ) => Promise<string>;
  updateTeamMember: (
    teamMemberId: string,
    fileData: Partial<
      Omit<
        TeamMember,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >
    >,
  ) => Promise<void>;
  deleteTeamMemberById: (teamMemberId: string) => Promise<void>;
  currentTeamMember?: TeamMember;
  loading: Loadings; // Optional loading state
  error: Errors; // Optional error state
}

const TeamMemberContext = createContext<TeamMemberContextProps | undefined>(
  undefined,
);

export const useTeamMember = (): TeamMemberContextProps => {
  const context = useContext(TeamMemberContext);
  if (!context) {
    throw new Error("useTeamMember must be used within a TeamMemberProvider");
  }
  return context;
};

export const TeamMemberProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter(); // Get the current matter ID from MatterContext
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [currentTeamMember, setCurrentTeamMember] = useState<TeamMember>();
  const [loading, setLoading] = useState<Loadings>({ list: false });
  const [error, setError] = useState<Errors>({ list: null });

  const fetchTeamMembers = async () => {
    if (!currentMatterId) return;
    const teamMembers = await MatterService.getTeamMembers(currentMatterId);
    setTeamMembers(teamMembers);
    setLoading((prev) => ({ ...prev, list: false }));
  };

  const fetchInvitations = async () => {
    if (!currentMatterId) return;
    const invitations = await MatterService.getInvitations(currentMatterId);
    setInvitations(invitations);
    setLoading((prev) => ({ ...prev, invitations: false }));
  };

  useSocketEventByIds(
    "memberships:changed",
    ["*"], // Listen to all changes for team members
    fetchTeamMembers, // Callback to fetch team members
  );

  useSocketEventByIds(
    "invitations:changed",
    ["*"],
    (updatedDocument: Invitation) => {
      console.log("Updated document:", updatedDocument);
      if (updatedDocument.matter !== currentMatterId) return;
      setInvitations((prev) =>
        prev.map((invitation) =>
          invitation._id === updatedDocument._id ? updatedDocument : invitation,
        ),
      );
    },
  );

  useEffect(() => {
    if (!currentMatterId) return;
    setLoading((prev) => ({ ...prev, list: true })); // Set loading state to true
    fetchTeamMembers();
    fetchInvitations();
  }, [currentMatterId]);

  useEffect(() => {
    if (!user) return;
    const currentTeamMember = teamMembers.find(
      (teamMember) => teamMember.user?._id === user.uid,
    );
    setCurrentTeamMember(currentTeamMember);
  }, [teamMembers, user]);

  const createTeamMember = async (
    teamMemberData: Omit<
      TeamMember,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdBy"
      | "matter"
      | "createdBy"
      | "updatedBy"
    >,
  ): Promise<string> => {
    const newTeamMemberId = await TeamMemberService.createTeamMember({
      ...teamMemberData,
      matter: currentMatterId!,
    });
    return newTeamMemberId;
  };

  const updateTeamMember = async (
    teamMemberId: string,
    fileData: Partial<
      Omit<
        TeamMember,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
      >
    >,
  ) => {
    await TeamMemberService.updateTeamMember(teamMemberId, fileData);
  };

  const deleteTeamMemberById = async (teamMemberId: string) => {
    await TeamMemberService.deleteTeamMemberById(teamMemberId);
    setTeamMembers((prev) =>
      prev.filter((teamMember) => teamMember._id !== teamMemberId),
    );
    setCurrentTeamMember(undefined);
  };

  return (
    <TeamMemberContext.Provider
      value={{
        loading,
        error,
        teamMembers,
        invitations,
        createTeamMember,
        updateTeamMember,
        deleteTeamMemberById,
        currentTeamMember,
      }}
    >
      {children}
    </TeamMemberContext.Provider>
  );
};
